import { useState } from "react";
import axios from "axios";
import { FileText, Zap, Download, CheckCircle, Clock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const REPORT_TYPES = [
  "Portfolio Performance Report",
  "Risk & Exposure Analysis",
  "Sector Attribution Report",
  "Analyst Consensus Summary",
  "Market Benchmark Comparison",
  "Year-End Investment Review",
];

const SECTIONS = [
  { key: "portfolio", label: "Portfolio Holdings" },
  { key: "market",   label: "Market Data" },
  { key: "sentiment", label: "Analyst Sentiment" },
];

export default function VBAKiller() {
  const [form, setForm] = useState({
    report_type: "Portfolio Performance Report",
    date_from: "2020-01-01",
    date_to: "2024-12-31",
    tickers: "",
    include_sections: ["portfolio", "market", "sentiment"],
  });
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(0);
  const [recentReports, setRecentReports] = useState([]);

  const toggleSection = (key) => {
    setForm(f => ({
      ...f,
      include_sections: f.include_sections.includes(key)
        ? f.include_sections.filter(s => s !== key)
        : [...f.include_sections, key]
    }));
  };

  const generate = async () => {
    setGenerating(true);
    setResult(null);
    setProgress(0);

    // Animate progress
    const interval = setInterval(() => {
      setProgress(p => p < 90 ? p + Math.random() * 15 : p);
    }, 150);

    try {
      const res = await axios.post(`${API}/report/generate`, {
        report_type: form.report_type,
        date_from: form.date_from,
        date_to: form.date_to,
        tickers: form.tickers ? form.tickers.split(",").map(t => t.trim()) : [],
        include_sections: form.include_sections,
      });
      setProgress(100);
      setResult(res.data);
      setRecentReports(prev => [res.data, ...prev.slice(0, 4)]);
    } catch (err) {
      alert("Report generation failed: " + (err.response?.data?.detail || err.message));
    } finally {
      clearInterval(interval);
      setGenerating(false);
    }
  };

  const download = (reportId) => {
    window.open(`${API}/report/download/${reportId}`, "_blank");
  };

  return (
    <div className="page-enter p-6 space-y-6" data-testid="vba-killer-page">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold tracking-tight">Ellerston Report Generator</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Generate institutional-grade PDF reports from centralized data
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form */}
        <div className="card-olive bg-white border border-border p-6" data-testid="report-form">
          <div className="flex items-center gap-2 mb-5">
            <FileText className="w-5 h-5 text-[#A8A67A]" strokeWidth={1.5} />
            <h3 className="font-semibold">Report Configuration</h3>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-foreground uppercase tracking-wider mb-1.5">
                Report Type
              </label>
              <select
                value={form.report_type}
                onChange={e => setForm(f => ({ ...f, report_type: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-border bg-background focus:outline-none focus:ring-1 focus:ring-[#A8A67A] rounded-none"
                data-testid="report-type-select"
              >
                {REPORT_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-foreground uppercase tracking-wider mb-1.5">
                  Date From
                </label>
                <input
                  type="date"
                  value={form.date_from}
                  onChange={e => setForm(f => ({ ...f, date_from: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-border bg-background focus:outline-none focus:ring-1 focus:ring-[#A8A67A] rounded-none"
                  data-testid="date-from-input"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground uppercase tracking-wider mb-1.5">
                  Date To
                </label>
                <input
                  type="date"
                  value={form.date_to}
                  onChange={e => setForm(f => ({ ...f, date_to: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-border bg-background focus:outline-none focus:ring-1 focus:ring-[#A8A67A] rounded-none"
                  data-testid="date-to-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground uppercase tracking-wider mb-1.5">
                Ticker Filter (optional, comma-separated)
              </label>
              <input
                type="text"
                value={form.tickers}
                onChange={e => setForm(f => ({ ...f, tickers: e.target.value }))}
                placeholder="AAPL, MSFT, NVDA"
                className="w-full px-3 py-2 text-sm border border-border bg-background focus:outline-none focus:ring-1 focus:ring-[#A8A67A] rounded-none placeholder:text-muted-foreground"
                data-testid="tickers-input"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground uppercase tracking-wider mb-2">
                Include Sections
              </label>
              <div className="space-y-2">
                {SECTIONS.map(s => (
                  <label key={s.key} className="flex items-center gap-2.5 cursor-pointer group">
                    <div
                      onClick={() => toggleSection(s.key)}
                      className={`w-4 h-4 border flex items-center justify-center transition-colors cursor-pointer ${
                        form.include_sections.includes(s.key)
                          ? "bg-[#A8A67A] border-[#A8A67A]"
                          : "border-border bg-background"
                      }`}
                      data-testid={`section-${s.key}`}
                    >
                      {form.include_sections.includes(s.key) && (
                        <CheckCircle className="w-2.5 h-2.5 text-white" />
                      )}
                    </div>
                    <span className="text-sm text-foreground">{s.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <button
              onClick={generate}
              disabled={generating}
              className="w-full flex items-center justify-center gap-2 bg-[#A8A67A] text-white py-3 text-sm font-semibold hover:bg-[#8E8C64] transition-colors disabled:opacity-60 disabled:cursor-not-allowed mt-2"
              data-testid="generate-report-btn"
            >
              <Zap className="w-4 h-4" />
              {generating ? "Generating Report..." : "Generate Report Instantly"}
            </button>
          </div>

          {/* Progress */}
          {generating && (
            <div className="mt-4">
              <div className="flex justify-between text-xs text-muted-foreground mb-1">
                <span>Querying warehouse &amp; building PDF...</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div className="h-1.5 bg-[#F5F4EE] overflow-hidden">
                <div
                  className="h-full bg-[#A8A67A] transition-all duration-150"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Result + Recent */}
        <div className="space-y-4">
          {result && (
            <div className="card-olive bg-white border border-border p-6" data-testid="report-result">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-5 h-5 text-emerald-500" />
                <h3 className="font-semibold text-emerald-700">Report Generated Successfully</h3>
              </div>
              <div className="space-y-3">
                <div className="bg-[#F5F4EE] p-3 text-center">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Generation Time</p>
                  <p className="text-2xl font-bold text-[#A8A67A]">{result.generation_time_seconds}s</p>
                </div>
                <div className="flex justify-between text-xs text-muted-foreground border border-border p-2">
                  <span>Report ID: <span className="font-mono font-semibold text-foreground">{result.report_id}</span></span>
                  <span>Size: <span className="font-mono">{result.file_size_kb} KB</span></span>
                </div>
                <button
                  onClick={() => download(result.report_id)}
                  className="w-full flex items-center justify-center gap-2 bg-[#A8A67A] text-white py-2.5 text-sm font-semibold hover:bg-[#8E8C64] transition-colors"
                  data-testid="download-report-btn"
                >
                  <Download className="w-4 h-4" />
                  Download PDF Report
                </button>
              </div>
            </div>
          )}

          {/* Recent reports */}
          {recentReports.length > 0 && (
            <div className="card-olive bg-white border border-border p-5" data-testid="recent-reports">
              <h3 className="font-semibold text-sm mb-3">Recent Reports</h3>
              <div className="space-y-2">
                {recentReports.map(r => (
                  <div key={r.report_id} className="flex items-center justify-between text-xs border border-border p-2.5 hover:bg-[#F8F9FA]">
                    <div>
                      <p className="font-mono font-semibold text-[#A8A67A]">{r.report_id}</p>
                      <p className="text-muted-foreground">{r.generation_time_seconds}s · {r.file_size_kb}KB</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-3 h-3 text-muted-foreground" />
                      <button onClick={() => download(r.report_id)}
                        className="text-[#A8A67A] hover:text-[#C4C29A] font-semibold"
                        data-testid={`download-${r.report_id}`}>
                        <Download className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
